import type {
  WorksheetContent,
  WorksheetExercise,
} from "@bindery/shared/contracts/worksheet";
import { QRCodeSVG } from "qrcode.react";

const QR_SIZE_PX = 77;

function Exercise({
  exercise,
  number,
}: {
  exercise: WorksheetExercise;
  number: number;
}) {
  const { sentence, target, options } = exercise;
  return (
    <li className="exercise flex break-inside-avoid gap-2 pb-[0.18in]">
      <span className="w-[0.22in] shrink-0 pt-[2pt] text-right font-sans text-[9pt] text-black/60">
        {number}
      </span>
      <div className="flex min-w-0 flex-col gap-[4pt]">
        <p className="font-serif text-[13pt] leading-[1.35]">
          {sentence.slice(0, target.start)}
          <span
            data-target
            className="border-ink-accent rounded-[3pt] border-[1.5pt] border-dashed px-[3pt] font-semibold"
          >
            {sentence.slice(target.start, target.end)}
          </span>
          {sentence.slice(target.end)}
        </p>
        <ul
          className="flex flex-wrap gap-x-[8pt] gap-y-[4pt]"
          aria-label="Words to circle"
        >
          {options.map((option) => (
            <li
              key={option}
              className="rounded-full border border-black/35 px-[8pt] py-[1pt] font-serif text-[12pt]"
            >
              {option}
            </li>
          ))}
        </ul>
      </div>
    </li>
  );
}

function NameLine({ printName }: { printName: string }) {
  return printName === "" ? (
    <span>
      Name <span className="inline-block w-[1.6in] border-b border-black" />
    </span>
  ) : (
    <span className="font-semibold">{printName}</span>
  );
}

/**
 * The printed page. Everything here is black on white, sized in points so
 * screen and paper agree, and the target is marked by a dashed box, never
 * by colour alone.
 */
export function WorksheetSheet({
  content,
  printName,
  gradeUrl,
  qrCarriesToken,
  sheetLabel,
}: {
  content: WorksheetContent;
  printName: string;
  gradeUrl: string;
  qrCarriesToken: boolean;
  sheetLabel: string;
}) {
  return (
    <article className="paper mx-auto flex w-full max-w-[8.27in] flex-col border bg-white p-6 text-black shadow-md sm:p-[0.75in]">
      <header className="flex flex-col gap-[6pt] border-b-[1.5pt] border-black pb-[8pt]">
        <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
          <h1 className="font-serif text-[20pt] font-semibold tracking-tight">
            Reading worksheet
          </h1>
          <p className="font-serif text-[12pt]">
            <NameLine printName={printName} />
            <span className="ml-[16pt]">
              Date{" "}
              <span className="inline-block w-[1.1in] border-b border-black" />
            </span>
          </p>
        </div>
        <p className="font-serif text-[11pt] text-black/75">
          Read each sentence aloud. Then circle the boxed word where it appears
          in the row under the sentence.
        </p>
      </header>

      <ol className="md:paper-columns print:paper-columns pt-[14pt]">
        {content.exercises.map((exercise, index) => (
          <Exercise
            key={`${index}-${exercise.sentence}`}
            exercise={exercise}
            number={index + 1}
          />
        ))}
      </ol>

      <footer className="mt-auto flex items-end justify-between gap-6 pt-[8pt]">
        <p className="max-w-[3.6in] font-sans text-[8.5pt] leading-snug text-black/60">
          Scan to record which words were read.{" "}
          {qrCarriesToken
            ? "The link opens once; after that the sheet is graded by signing in."
            : "A reprint’s link has no one-time code, so it asks the adult who made the sheet to sign in."}
          <br />
          Sheet {sheetLabel}
        </p>
        <div className="flex flex-col items-center gap-[2pt]">
          <QRCodeSVG
            value={gradeUrl}
            size={QR_SIZE_PX}
            level="M"
            bgColor="#ffffff"
            fgColor="#000000"
            title="Grade this worksheet"
          />
          <span className="font-sans text-[7pt] text-black/60">
            Scan to grade
          </span>
        </div>
      </footer>
    </article>
  );
}
